/* eslint-disable no-lonely-if */

import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Ps } from '../../../../api/model';
import { toString } from '../../../../utils/string';
import { CheckBox } from '../../../atoms/CheckBox';
import SingleSelect from '../../../atoms/SingleSelect';
import { TextInput } from '../../../atoms/TextInput';
import { DiffType, PsDetailsMode } from './constants/types';
import { PsSchemaType } from './constants/variables';
import PsDiffViewerDialog from './PsDiffViewerDialog';

interface Props {
  /** PS 閲覧・追加・編集の内、どのモードかを示す。 */
  mode: PsDetailsMode;

  /** 表示・編集対象の PS。 */
  ps: Ps;

  /** PS 比較で使用する。比較対象となる、旧バージョンの PS。 */
  olderPs?: Ps | undefined;
}

// PsDetails が長くなるので切り分けた部分。
/** 各 PS の属性情報。 */
function PsAttributes({ mode, ps, olderPs }: Props) {
  // i18n 用。
  const { t } = useTranslation();

  // 参照: https://qiita.com/y-yasuda/items/9184380d3320d47bc564
  // PsDetails に置かれた FormProvider から取得。
  const { register, formState } = useFormContext<PsSchemaType>();

  /** 追加・編集状態。 */
  const isAddingOrEditing = mode === 'Add' || mode === 'Edit';

  // ps.level は null であることもあるようなので (特にルート PS と章 PS)、type で判別。
  /** 「ORIAS_J2_テーブル設計書.xlsx」で「L0、L1PSの場合はNULL」、などと表記されている属性では、編集画面ではコントロールを表示しない。その判別条件。 */
  const isL0L1Ps = Number(ps.type) <= 1; // ((ps.type === 0) || (ps.type === 1));

  /** SingleSelect に渡す関数。 */
  const getValue = (valueLabelPair: string) => valueLabelPair.split(':')[0];
  /** SingleSelect に渡す関数。 */
  const getLabel = (valueLabelPair: string) => valueLabelPair.split(':')[1];

  // 「ORIAS_画面レイアウト仕様詳細_1.0_PS.xlsx」の「値参照」列を見て、参照元テーブルから「ORIAS_J2_テーブル設計書.xlsx」のシートを参照、数値に対応する文言に変換して表示。
  /** 引数のラベルと値に対応した文言を含んだ li 要素を返す関数。 */
  const getPsAttributeLiElement = useCallback(
    (
      label: string,
      // t() の引数を static なものにするため、caller 側から渡す。
      tLabel: string,
      value: number | boolean | string | undefined,
      olderValue: number | boolean | string | undefined = undefined
    ) => {
      // PS 比較画面: PS 詳細情報については、新旧 PS データで結果が異なる部分を、追加・変更された情報は黄色、削除された情報は赤色でハイライト表示する。
      let diffType: DiffType = 'NoChange';
      if (mode === 'Diff') {
        if (
          typeof olderValue === 'undefined' ||
          olderValue === null
          // ||
          // (
          //   (typeof olderValue === 'string')
          //   &&
          //   (olderValue.length === 0)
          // )
        ) {
          // olderValue が空の場合。

          if (!(typeof value === 'undefined' || value === null)) {
            // value が空じゃない場合。
            diffType = 'Added';
          }
        } else {
          // olderValue が空じゃない場合。

          if (
            typeof value === 'undefined' ||
            value === null
            // ||
            // (
            //   (typeof value === 'string')
            //   &&
            //   (value.length === 0)
            // )
          ) {
            // value が空になった場合。
            diffType = 'Deleted';
          } else if (olderValue !== value) {
            // olderValue、value どちらも空ではなく、値が異なる場合。
            diffType = 'Modified';
          }
        }
      }

      const valueToShow = diffType === 'Deleted' ? olderValue : value;

      // 表示する文言を Map に入れる。key はラベル、value は配列。
      // この関数の引数の value が数値の場合はその配列の index とする。
      const labelValueTextMap = new Map();
      labelValueTextMap.set(label, ['']);

      let index = 0;

      if (typeof valueToShow === 'undefined' || valueToShow === null) {
        // 値が無ければ表示しない。
        return undefined;
      }

      if (typeof valueToShow === 'string') {
        if (valueToShow.length === 0) {
          // 空の場合は非表示。
          return undefined;
        }
        labelValueTextMap.set(label, [valueToShow]);
      }

      if (typeof valueToShow === 'boolean') {
        // 0 (false): 非表示、1 (true): "〇"。
        if (valueToShow === false) {
          return undefined;
        }
        labelValueTextMap.set(label, [t('〇')]);
      }

      if (typeof valueToShow === 'number') {
        // 数値の場合は、文言を配列にし、数値を index とする。
        index = valueToShow;
      }

      // ラベルに応じた個別の対応。文言を配列に入れる。

      labelValueTextMap.set('バージョン', [toString(valueToShow)]);
      if (label === 'バージョン') {
        index = 0;
      }

      // フロントエンドでは日付は「/」で区切る。
      // バックエンドから送られる日付の形式は OpenAPI の仕様で固定されていて、yyyy-MM-dd らしい。
      labelValueTextMap.set('公開日時', [toString(valueToShow).replaceAll('-', '/')]);

      // ps_l1_outlines.level
      // L2のタイトルPSの子にコンテンツPSとタイトルPSが居る場合、コンテンツPSは2(L2)、タイトルPSは3(L3)となる
      // ただし、ここに設定されるのはL2～L5のみ
      // 0：ルート
      // 1：L1
      // 2：L2
      // 3：L3
      // 4：L4
      // 5：L5
      {
        const valueTexts = new Array(6);
        valueTexts.splice(0, 1, t('ルート'));
        // valueTexts[index] に Lx を挿入。
        // t() は不要？
        valueTexts.splice(index, 1, `L${index}`);
        labelValueTextMap.set('タイトルレベル', valueTexts);
      }

      // ps_l1_outlines.type
      // 改廃案で作成したPSは承認まではアウトライン上にのみ存在するため種別を保持する必要がある
      // 0：ルートPS
      // 1：章PS（L1）
      // 2：タイトルPS
      // 3：コンテンツPS
      labelValueTextMap.set('PS種別', [t('ルートPS'), t('章PS（L1）'), t('タイトルPS'), t('コンテンツPS')]);

      // ps_versions.text_type
      // L0、L1PSの場合はNULL
      // 0：無し
      // 1：注意
      // 2：警告
      // 3：危険
      // 4：アドバイス
      labelValueTextMap.set('安全表示', [t('無し'), t('注意'), t('警告'), t('危険'), t('アドバイス')]);

      // ps_versions.content_type
      // L0、L1PSの場合はNULL
      // 0：無し
      // 1：本文
      // 2：本文補足
      // 3：作動する条件
      // 4：作動しない条件
      // 5：カスタマイズ
      // 6：注意・警告
      // 7：指示事項
      // 8：こんなときは
      labelValueTextMap.set('記載内容分類', [
        t('無し'),
        t('本文'),
        t('本文補足'),
        t('作動する条件'),
        t('作動しない条件'),
        t('カスタマイズ'),
        t('注意・警告'),
        t('指示事項'),
        t('こんなときは'),
      ]);

      /**
       * 表示する値を取得
       * @param valueShow
       * @returns
       */
      const getDiffValue = (valueShow: string | number | boolean | undefined) => {
        const labelValueTextMapSub = new Map();
        labelValueTextMapSub.set(label, ['']);

        let indexSub = 0;

        if (typeof valueShow === 'undefined' || valueShow === null) {
          // 値が無ければ表示しない。
          return '';
        }

        if (typeof valueShow === 'string') {
          if (valueShow.length === 0) {
            // 空の場合は非表示。
            return '';
          }
          labelValueTextMapSub.set(label, [valueShow]);
        }

        if (typeof valueShow === 'boolean') {
          // 0 (false): 非表示、1 (true): "〇"。
          if (valueShow === false) {
            return '';
          }
          labelValueTextMapSub.set(label, [t('〇')]);
        }

        if (typeof valueShow === 'number') {
          // 数値の場合は、文言を配列にし、数値を index とする。
          indexSub = valueShow;
        }

        // ラベルに応じた個別の対応。文言を配列に入れる。

        labelValueTextMapSub.set('バージョン', [toString(valueShow)]);
        if (label === 'バージョン') {
          indexSub = 0;
        }

        // フロントエンドでは日付は「/」で区切る。
        // バックエンドから送られる日付の形式は OpenAPI の仕様で固定されていて、yyyy-MM-dd らしい。
        labelValueTextMapSub.set('公開日時', [toString(valueShow).replaceAll('-', '/')]);

        // ps_l1_outlines.level
        // L2のタイトルPSの子にコンテンツPSとタイトルPSが居る場合、コンテンツPSは2(L2)、タイトルPSは3(L3)となる
        // ただし、ここに設定されるのはL2～L5のみ
        // 0：ルート
        // 1：L1
        // 2：L2
        // 3：L3
        // 4：L4
        // 5：L5
        {
          const valueTexts = new Array(6);
          valueTexts.splice(0, 1, t('ルート'));
          // valueTexts[index] に Lx を挿入。
          // t() は不要？
          valueTexts.splice(indexSub, 1, `L${indexSub}`);
          labelValueTextMapSub.set('タイトルレベル', valueTexts);
        }

        // ps_l1_outlines.type
        // 改廃案で作成したPSは承認まではアウトライン上にのみ存在するため種別を保持する必要がある
        // 0：ルートPS
        // 1：章PS（L1）
        // 2：タイトルPS
        // 3：コンテンツPS
        labelValueTextMapSub.set('PS種別', [t('ルートPS'), t('章PS（L1）'), t('タイトルPS'), t('コンテンツPS')]);

        // ps_versions.text_type
        // L0、L1PSの場合はNULL
        // 0：無し
        // 1：注意
        // 2：警告
        // 3：危険
        // 4：アドバイス
        labelValueTextMapSub.set('安全表示', [t('無し'), t('注意'), t('警告'), t('危険'), t('アドバイス')]);

        // ps_versions.content_type
        // L0、L1PSの場合はNULL
        // 0：無し
        // 1：本文
        // 2：本文補足
        // 3：作動する条件
        // 4：作動しない条件
        // 5：カスタマイズ
        // 6：注意・警告
        // 7：指示事項
        // 8：こんなときは
        labelValueTextMapSub.set('記載内容分類', [
          t('無し'),
          t('本文'),
          t('本文補足'),
          t('作動する条件'),
          t('作動しない条件'),
          t('カスタマイズ'),
          t('注意・警告'),
          t('指示事項'),
          t('こんなときは'),
        ]);
        return String(labelValueTextMapSub.get(label)[indexSub]);
      };

      // 比較用
      const valueNew = getDiffValue(value ?? '');
      const valueOld = getDiffValue(olderValue ?? '');

      return (
        <PsAttributeLi mode={mode} label={tLabel} diffType={diffType} newValue={valueNew} oldValue={valueOld}>
          {labelValueTextMap.get(label)[index]}
        </PsAttributeLi>
      );
    },
    [mode, t]
  );

  return (
    <div className="psiconlist" id="anker-infomation">
      <ul>
        {getPsAttributeLiElement('管理No.', t('管理No.'), ps.ps_no, olderPs?.ps_no)}
        {getPsAttributeLiElement('バージョン', t('バージョン'), ps.version_no, olderPs?.version_no)}
        {getPsAttributeLiElement('公開日時', t('公開日時'), ps.release_at, olderPs?.release_at)}
        {getPsAttributeLiElement('タイトルレベル', t('タイトルレベル'), ps.level, olderPs?.level)}
        {getPsAttributeLiElement('PS種別', t('PS種別'), ps.type, olderPs?.type)}
        {
          // text_type。
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('安全表示')} additionalClassNameOfValueSpan="ddfr">
                <SingleSelect
                  name={'text_type' as never}
                  allowEmpty={false}
                  // 0: 無し
                  // 1: 注意
                  // 2: 警告
                  // 3: 危険
                  // 4: アドバイス
                  options={[
                    `:${toString(t('選択してください'))}`,
                    `0:${toString(t('無し'))}`,
                    `1:${toString(t('注意'))}`,
                    `2:${toString(t('警告'))}`,
                    `3:${toString(t('危険'))}`,
                    `4:${toString(t('アドバイス'))}`,
                  ]}
                  register={register}
                  getValue={getValue}
                  getLabel={getLabel}
                />
              </PsAttributeLi>
            ) : (
              // (
              //   <li>
              //     <span className='labelfr'>{t('安全表示')}</span>
              //     <span className='valuefr ddfr'>

              //     </span>
              //   </li>
              // )
              getPsAttributeLiElement('安全表示', t('安全表示'), ps.text_type, olderPs?.text_type)
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('安全表示（自由記述）')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput name={'text_type_description' as never} register={register} errors={formState.errors} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement(
                '安全表示（自由記述）',
                t('安全表示（自由記述）'),
                ps.text_type_description,
                olderPs?.text_type_description
              )
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('表形式')}>
                <CheckBox name={'table_exist' as never} register={register} />
              </PsAttributeLi>
            ) : (
              // type は boolean ということになっているが、number で送られてきているので、Boolean() で boolean に変換。
              getPsAttributeLiElement('表形式', t('表形式'), Boolean(ps.table_exist), Boolean(olderPs?.table_exist))
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('重要度')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput name={'importance' as never} register={register} errors={formState.errors} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('重要度', t('重要度'), ps.importance, olderPs?.importance)
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('機種固有')}>
                <CheckBox name={'specific' as never} register={register} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('機種固有', t('機種固有'), Boolean(ps.specific), Boolean(olderPs?.specific))
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('反映必須項目')}>
                <CheckBox name={'reflect_require' as never} register={register} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement(
                '反映必須項目',
                t('反映必須項目'),
                Boolean(ps.reflect_require),
                Boolean(olderPs?.reflect_require)
              )
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('記載内容分類')} additionalClassNameOfValueSpan="ddfr">
                <SingleSelect
                  name={'content_type' as never}
                  allowEmpty={false}
                  // ps_versions.content_type
                  // 0: 無し
                  // 1: 本文
                  // 2: 本文補足
                  // 3: 作動する条件
                  // 4: 作動しない条件
                  // 5: カスタマイズ
                  // 6: 注意・警告
                  // 7: 指示事項
                  // 8: こんなときは
                  options={[
                    `:${toString(t('選択してください'))}`,
                    `0:${toString(t('無し'))}`,
                    `1:${toString(t('本文'))}`,
                    `2:${toString(t('本文補足'))}`,
                    `3:${toString(t('作動する条件'))}`,
                    `4:${toString(t('作動しない条件'))}`,
                    `5:${toString(t('カスタマイズ'))}`,
                    `6:${toString(t('注意・警告'))}`,
                    `7:${toString(t('指示事項'))}`,
                    `8:${toString(t('こんなときは'))}`,
                  ]}
                  register={register}
                  getValue={getValue}
                  getLabel={getLabel}
                />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('記載内容分類', t('記載内容分類'), ps.content_type, olderPs?.content_type)
            ))
        }
        {/* 入力不可。PS内の派生コンテンツにイラストが含まれるかどうかを改廃案保存時にチェックしてこの値をセットする。 */}
        {getPsAttributeLiElement('イラスト', t('イラスト'), Boolean(ps.illust_exist), Boolean(olderPs?.illust_exist))}
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('大きいイラスト')}>
                <CheckBox name={'large_illust_exist' as never} register={register} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement(
                '大きいイラスト',
                t('大きいイラスト'),
                Boolean(ps.large_illust_exist),
                Boolean(olderPs?.large_illust_exist)
              )
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi mode={mode} label={t('イラストの横に回り込むイラスト')}>
                <CheckBox name={'illust_side_text_exist' as never} register={register} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement(
                'イラストの横に回り込むイラスト',
                t('イラストの横に回り込むイラスト'),
                Boolean(ps.illust_side_text_exist),
                Boolean(olderPs?.illust_side_text_exist)
              )
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('開発部門担当室課')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput name={'hgt_section' as never} register={register} errors={formState.errors} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('開発部門担当室課', t('開発部門担当室課'), ps.hgt_section, olderPs?.hgt_section)
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('開発部門担当者名')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput name={'hgt_person' as never} register={register} errors={formState.errors} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('開発部門担当者名', t('開発部門担当者名'), ps.hgt_person, olderPs?.hgt_person)
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('開発部門質問カテゴリ')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput name={'hgt_category' as never} register={register} errors={formState.errors} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement(
                '開発部門質問カテゴリ',
                t('開発部門質問カテゴリ'),
                ps.hgt_category,
                olderPs?.hgt_category
              )
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('移行先PS管理No.')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput
                  name={'next_ps_no' as never}
                  register={register}
                  errors={formState.errors}
                  textInputStyle={{
                    minWidth: '25em',
                  }}
                />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('移行先PS管理No.', t('移行先PS管理No.'), ps.next_ps_no, olderPs?.next_ps_no)
            ))
        }
        {
          // L0、L1 PS の場合は NULL。
          !isL0L1Ps &&
            (isAddingOrEditing ? (
              <PsAttributeLi
                mode={mode}
                label={t('FSR ID')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput name={'fsr_id' as never} register={register} errors={formState.errors} />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('FSR ID', t('FSR ID'), ps.fsr_id, olderPs?.fsr_id)
            ))
        }
        {
          // 追加・編集画面のみ表示。
          isAddingOrEditing &&
            // L1 PS のみ表示。新規作成時のみテキストボックス（必須入力）とし、それ以外はラベル（参照のみ。変更不可）とする。
            ps.type === 1 &&
            (mode === 'Add' ? (
              <PsAttributeLi
                mode={mode}
                label={t('管理No.接頭語')}
                classNameOfLi="probg"
                additionalClassNameOfValueSpan="ddfr"
              >
                <TextInput
                  name={'ps_no_prefix' as never}
                  register={register}
                  // エラー メッセージは PsDetails のモーダルで表示する。
                  // errors={formState.errors}
                />
              </PsAttributeLi>
            ) : (
              getPsAttributeLiElement('管理No.接頭語', t('管理No.接頭語'), ps.ps_no_prefix, olderPs?.ps_no_prefix)
            ))
        }
      </ul>
    </div>
  );
}

PsAttributes.defaultProps = {
  olderPs: undefined,
};

export default PsAttributes;

/** PS 属性情報用の li 要素。 */
function PsAttributeLi({
  mode,
  children,
  label,
  classNameOfLi,
  additionalClassNameOfValueSpan,
  diffType,
  newValue,
  oldValue,
}: {
  mode: PsDetailsMode | undefined;
  children: React.ReactNode;
  label: string;
  classNameOfLi?: string | undefined;
  additionalClassNameOfValueSpan?: string | undefined;
  diffType?: DiffType | undefined;
  newValue?: string | undefined;
  oldValue?: string | undefined;
}) {
  /** 背景色。 */
  let backgroundColor = 'initial';

  // PS 比較画面: PS 詳細情報については、新旧 PS データで結果が異なる部分を、追加・変更された情報は黄色、削除された情報は赤色でハイライト表示する。
  switch (diffType) {
    case 'Added':
    case 'Modified':
      backgroundColor = 'Yellow';
      break;

    case 'Deleted':
      backgroundColor = 'OrangeRed';
      break;

    default:
      break;
  }

  const [isDiffDlgOpen, setIsDiffDlgOpen] = useState(false);

  return (
    <>
      <li
        className={`${toString(classNameOfLi)}`}
        onClick={() => {
          if (mode === 'Diff' && diffType !== 'NoChange') {
            setIsDiffDlgOpen(true);
          }
        }}
      >
        <span className="labelfr">{label}</span>
        <span
          className={`valuefr ${toString(additionalClassNameOfValueSpan)}`}
          style={{
            backgroundColor,
          }}
        >
          {children}
        </span>
      </li>
      {/* PS比較ダイアログ。 */}
      {isDiffDlgOpen && (
        <PsDiffViewerDialog
          isOpen={isDiffDlgOpen}
          setIsOpen={setIsDiffDlgOpen}
          titleText={label}
          langugeId={-1}
          oldText={oldValue}
          newText={newValue}
          oldTitle="変更前"
          newTitle="変更後"
          isXml={false}
        />
      )}
    </>
  );
}

PsAttributeLi.defaultProps = {
  classNameOfLi: undefined,
  additionalClassNameOfValueSpan: undefined,
  diffType: undefined,
  newValue: '',
  oldValue: '',
};
